import { DEFAULT_LANGUAGE, isSupportedLanguage, type Language } from "../i18n/i18n";

export type SavedGameMode = "offline" | "online";

export type SavedProfile = {
  name: string;
  ballColor: string;
  nameColor: string;
};

export type SavedSettings = {
  language: Language;
  lastMode: SavedGameMode;
  profile: SavedProfile;
  soundEnabled: boolean;
};

export const PLAYER_NAME_MAX_LENGTH = 32;

const STORAGE_KEY = "survivrr.settings.v1";
const DEFAULT_NAME_PREFIX = "Player";

export class LocalSettingsStore {
  public load(): SavedSettings {
    const defaults = defaultSettings();

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);

      if (!raw) {
        this.save(defaults);
        return defaults;
      }

      const parsed = JSON.parse(raw) as Partial<SavedSettings>;
      const rawName = typeof parsed.profile?.name === "string" ? parsed.profile.name : defaults.profile.name;

      const merged: SavedSettings = {
        language: isSupportedLanguage(parsed.language) ? parsed.language : DEFAULT_LANGUAGE,
        lastMode: parsed.lastMode === "offline" ? "offline" : "online",
        profile: {
          name: isLegacyExactPlayerName(rawName) ? generatePlayerName() : sanitizeName(rawName),
          ballColor: normalizeHex(parsed.profile?.ballColor ?? defaults.profile.ballColor),
          nameColor: normalizeHex(parsed.profile?.nameColor ?? defaults.profile.nameColor)
        },
        soundEnabled: typeof parsed.soundEnabled === "boolean" ? parsed.soundEnabled : true
      };

      this.save(merged);
      return merged;
    } catch {
      this.save(defaults);
      return defaults;
    }
  }

  public save(settings: SavedSettings): void {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }

  public update(patch: Partial<SavedSettings>): SavedSettings {
    const current = this.load();

    const next: SavedSettings = {
      language: patch.language ?? current.language,
      lastMode: patch.lastMode ?? current.lastMode,
      profile: {
        ...current.profile,
        ...(patch.profile ?? {})
      },
      soundEnabled: patch.soundEnabled ?? current.soundEnabled
    };

    next.profile.name = sanitizeName(next.profile.name);
    next.profile.ballColor = normalizeHex(next.profile.ballColor);
    next.profile.nameColor = normalizeHex(next.profile.nameColor);

    this.save(next);
    return next;
  }
}

export function normalizeName(value: string): string {
  const sanitized = sanitizeName(value);
  return sanitized.length > 0 ? sanitized : generatePlayerName();
}

export function sanitizeName(value: string): string {
  return trimToMaxNameLength(
    value
      .replace(/[^\p{L}\p{N}_ .-]/gu, "")
      .replace(/\s+/g, " ")
      .trimStart()
  );
}

export function nameForJoin(value: string): string {
  const sanitized = sanitizeName(value).trim();
  return sanitized.length > 0 ? sanitized : generatePlayerName();
}

export function chooseReadableNameColor(backgroundHex: string): string {
  const { r, g, b } = hexToRgb(backgroundHex);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.58 ? "#000000" : "#ffffff";
}

export function generatePlayerName(): string {
  return `${DEFAULT_NAME_PREFIX}${10000 + cryptoRandomInt(90000)}`;
}

function defaultSettings(): SavedSettings {
  const ballColor = randomColor();

  return {
    language: "en",
    lastMode: "online",
    profile: {
      name: generatePlayerName(),
      ballColor,
      nameColor: chooseReadableNameColor(ballColor)
    },
    soundEnabled: true
  };
}

function isLegacyExactPlayerName(value: string): boolean {
  return value.trim() === "Player";
}

function trimToMaxNameLength(value: string): string {
  return Array.from(value).slice(0, PLAYER_NAME_MAX_LENGTH).join("");
}

function randomColor(): string {
  const palette = [
    "#4287f5",
    "#ef4444",
    "#22c55e",
    "#f59e0b",
    "#8b5cf6",
    "#ec4899",
    "#14b8a6",
    "#64748b",
    "#e11d48",
    "#0ea5e9"
  ] as const;

  return pick(palette);
}

function pick<T>(items: readonly T[]): T {
  const item = items[cryptoRandomInt(items.length)];

  if (item === undefined) {
    throw new Error("Cannot pick from an empty array");
  }

  return item;
}

function cryptoRandomInt(maxExclusive: number): number {
  const safeMax = Math.max(1, Math.floor(maxExclusive));

  if (globalThis.crypto?.getRandomValues) {
    const values = new Uint32Array(1);
    globalThis.crypto.getRandomValues(values);
    const value = values[0] ?? 0;
    return value % safeMax;
  }

  return Math.floor(Math.random() * safeMax);
}

function normalizeHex(value: string): string {
  const input = value.trim().toLowerCase();
  return /^#[0-9a-f]{6}$/.test(input) ? input : "#4287f5";
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = normalizeHex(hex).replace("#", "");
  const number = Number.parseInt(normalized, 16);

  return {
    r: (number >> 16) & 255,
    g: (number >> 8) & 255,
    b: number & 255
  };
}